/**
 * Phase 14.5 — QorBOM Docs · Left-rail nested navigation.
 *
 * The Stripe/Mintlify left rail: sectioned link list with active-route
 * highlighting. Routes are NOT hard-coded here as plain strings — the
 * caller passes a sections tree so the schema can grow without
 * touching the rail.
 *
 * Mobile (≤md): the rail collapses into a "Docs menu" disclosure on
 * top of the page content instead of next to it.
 */
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";


export default function LeftRail({ sections, testid }) {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Close mobile rail when the route changes.
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  return (
    <nav
      data-testid={testid || "qorbom-docs-leftrail"}
      aria-label="Documentation sections"
    >
      {/* Mobile toggle (visible <md) */}
      <button
        type="button"
        onClick={() => setMobileOpen((v) => !v)}
        className="mb-4 flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-900 md:hidden"
        data-testid="qorbom-docs-leftrail-mobile-toggle"
      >
        <span>Docs menu</span>
        {mobileOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      <div className={`${mobileOpen ? "block" : "hidden"} md:block`}>
        {sections.map((sec) => (
          <RailSection key={sec.title} section={sec} pathname={pathname} />
        ))}
      </div>
    </nav>
  );
}


function RailSection({ section, pathname }) {
  return (
    <div className="mb-8">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500"
           data-testid={`qorbom-docs-leftrail-section-${section.id}`}>
        {section.title}
      </div>
      <ul className="space-y-0.5">
        {section.links.map((link) => {
          const isActive =
            pathname === link.to ||
            (link.matchPrefix && pathname.startsWith(link.matchPrefix));
          return (
            <li key={link.to}>
              <Link
                to={link.to}
                className={
                  "block rounded px-2 py-1.5 text-sm transition-colors " +
                  (isActive
                    ? "bg-blue-50 font-semibold text-blue-800 border-l-2 border-blue-700 -ml-[2px] pl-[6px]"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-900")
                }
                data-testid={`qorbom-docs-leftrail-link-${link.id}`}
              >
                {link.title}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


// The schema the docs shell uses today. Add a new page by appending
// here + creating the route — no other file touched.
export const QORBOM_DOCS_SECTIONS = [
  {
    id: "get-started",
    title: "Get started",
    links: [
      { id: "quickstart", title: "Quickstart",     to: "/qorbom/api-docs/quickstart",
        matchPrefix: "/qorbom/api-docs/quickstart" },
      { id: "auth",       title: "Authentication", to: "/qorbom/api-docs/auth",
        matchPrefix: "/qorbom/api-docs/auth" },
      { id: "sdks",       title: "SDKs & samples", to: "/qorbom/api-docs/sdks",
        matchPrefix: "/qorbom/api-docs/sdks" },
    ],
  },
  {
    id: "api-reference",
    title: "API reference",
    links: [
      { id: "endpoints",  title: "Endpoints",      to: "/qorbom/api-docs/endpoints",
        matchPrefix: "/qorbom/api-docs/endpoints" },
      { id: "webhooks",   title: "Webhooks",       to: "/qorbom/api-docs/webhooks",
        matchPrefix: "/qorbom/api-docs/webhooks" },
      { id: "errors",     title: "Error codes",    to: "/qorbom/api-docs/errors",
        matchPrefix: "/qorbom/api-docs/errors" },
    ],
  },
  {
    id: "interactive",
    title: "Interactive",
    links: [
      { id: "api-reference-swagger", title: "Live API reference",
        to: "/qorbom/api-reference",
        matchPrefix: "/qorbom/api-reference" },
    ],
  },
];
